import classNames from 'classnames'
import { motion } from 'motion/react'
import { memo, useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import type { UIEvent } from 'react'
import { useTranslation } from 'react-i18next'

import {
  MarketDataBridge,
  updateCurrentPricesFromMarketData,
} from '@pages/Trade/Portfolio/components/MarketDataBridge'
import {
  initializeCurrentPrices,
  useCurrentPriceForTrade,
} from '@pages/Trade/Portfolio/hooks/useCurrentPricesStore'
import { formatSymbol } from '@pages/Trade/Portfolio/utils/formatters'
import { useTradingSessionStatus } from '@pages/Trade/Widget/hooks/useTradingSessionStatus'
import { useMarketDataEvents } from '@pages/Trade/hooks/useMarketDataEvents'
import { usePriceColor } from '@pages/Trade/hooks/usePriceColor'
import { getStepDecimals } from '@pages/Trade/utils/helpers'

import { Truncate } from '@ui/Truncate/Truncate'
import { Body1, Body2 } from '@ui/Typography'
import { Button, IconButton } from '@ui/components/Button'
import { Tooltip } from '@ui/components/Tooltip'
import { InfoIcon } from '@ui/icons/Info.icon'
import { MoonIcon } from '@ui/icons/Moon.icon'
import { NewspaperIcon } from '@ui/icons/Newspaper.icon'
import { Scrollbar } from '@ui/v2/Scrollbar/Scrollbar'
import { SkeletonRow } from '@ui/v2/Skeleton'
import { Table, TableBody, TableCell, TableNoResults, TableRow } from '@ui/v2/Table/Table'

import { useWindowResize } from '@utils/domUtils'

import type {
  DxTradeInstrumentDetails,
  DxTradeInstrumentWithTradingMeta,
  DxTradeRawCandle,
  InstrumentActionHandler,
  InstrumentActionType,
} from '@model/DxTrade.types'
import {
  MarketDataEventFormats,
  MarketDataEventTypes,
  type MarketDataRequest,
} from '@model/DxTrade.types'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'

import { InstrumentCard } from './InstrumentCard'
import { InstrumentCardSkeleton } from './InstrumentCardSkeleton'
import { Sparkline, SparklineSkeleton } from './components/Sparkline'
import { getFavoriteIcon, isFavoriteInstrument } from './helpers'
import { useChangePercent } from './hooks/useChangePercent'
import { useDxtradeLatestCandles } from './hooks/useDxtradeLatestCandles'
import { useSparklineData } from './hooks/useSparklineData'

import styles from './Instruments.module.scss'

type InstrumentsTableProps = {
  accountCode?: string
  instruments: DxTradeInstrumentWithTradingMeta[]
  isInitialLoading: boolean
  isLoadingMore: boolean
  hasNext: boolean
  loadNext: () => void
  selectedSymbol?: string
  onInstrumentAction?: InstrumentActionHandler
  onSelect?: (symbol: string, priceIncrement?: number | null) => void
  favoriteSymbols?: string[]
  onToggleFavorite?: (symbol: string) => void
  collapsed?: boolean
  onToggleCollapse: (collapsed: boolean) => void
  isOrderWidgetOpen?: boolean
  watchlistEmptyState?: ReactNode
}

const PRICE_PLACEHOLDER = '—'
const SCROLL_SIZE_EPSILON_PX = 1
const BOTTOM_LOAD_THRESHOLD_PX = 32
// how many extra pages we're allowed to auto-fetch to fill a non-scrollable
const MAX_AUTOFILL_PAGES = 3

const MARKET_DATA_EVENT_TYPES: MarketDataRequest['eventTypes'] = [
  {
    type: MarketDataEventTypes.QUOTE,
    format: MarketDataEventFormats.COMPACT,
  },
]

const InstrumentsTable = ({
  accountCode,
  instruments,
  isInitialLoading,
  isLoadingMore,
  hasNext,
  loadNext,
  selectedSymbol,
  onInstrumentAction,
  onSelect,
  favoriteSymbols,
  onToggleFavorite,
  collapsed = false,
  onToggleCollapse,
  isOrderWidgetOpen = false,
  watchlistEmptyState,
}: InstrumentsTableProps) => {
  const isMobile = useWindowResize()
  const { t } = useTranslation()

  const [subscribedSymbols, setSubscribedSymbols] = useState<string[]>([])

  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const [isVerticalScrollable, setIsVerticalScrollable] = useState(false)
  const [isHorizontalScrollable, setIsHorizontalScrollable] = useState(false)
  const lastScrollTopRef = useRef(0)
  const edgeLockRef = useRef<'bottom' | null>(null)
  const autofillCountRef = useRef(0)
  const mobileLoadMoreRef = useRef<HTMLDivElement | null>(null)
  const mobileObserverRef = useRef<IntersectionObserver | null>(null)

  const prevCollapsedRef = useRef(collapsed)

  useEffect(() => {
    if (isInitialLoading) {
      edgeLockRef.current = null
      autofillCountRef.current = 0
    }
  }, [isInitialLoading])

  useEffect(() => {
    if (isMobile) {
      setIsVerticalScrollable(false)
      setIsHorizontalScrollable(false)
      return
    }

    const updateScrollable = () => {
      const container = scrollContainerRef.current
      if (!container) {
        return
      }

      if (prevCollapsedRef.current !== collapsed) {
        edgeLockRef.current = null
        prevCollapsedRef.current = collapsed
      }

      const isScrollable = container.scrollHeight > container.clientHeight + SCROLL_SIZE_EPSILON_PX
      setIsVerticalScrollable(isScrollable)
      setIsHorizontalScrollable(
        container.scrollWidth > container.clientWidth + SCROLL_SIZE_EPSILON_PX
      )

      if (!isScrollable && edgeLockRef.current === 'bottom') {
        edgeLockRef.current = null
      }

      const isAtBottom =
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - BOTTOM_LOAD_THRESHOLD_PX

      lastScrollTopRef.current = container.scrollTop

      // auto-fetch rules after the page resize refactor
      const canFetch =
        !isInitialLoading && !isLoadingMore && hasNext && edgeLockRef.current !== 'bottom'

      if (canFetch) {
        if (isScrollable && isAtBottom) {
          edgeLockRef.current = 'bottom'
          loadNext()
        } else if (!isScrollable && autofillCountRef.current < MAX_AUTOFILL_PAGES) {
          autofillCountRef.current += 1
          edgeLockRef.current = 'bottom'
          loadNext()
        }
      }
    }

    updateScrollable()

    window.addEventListener('resize', updateScrollable)

    return () => {
      window.removeEventListener('resize', updateScrollable)
    }
  }, [isMobile, instruments.length, isInitialLoading, isLoadingMore, hasNext, loadNext, collapsed])

  useEffect(() => {
    const nextSymbols = Array.from(
      new Set([
        ...instruments.map((item) => item.symbol).filter((symbol): symbol is string => !!symbol),
        ...(selectedSymbol ? [selectedSymbol] : []),
      ])
    )

    setSubscribedSymbols(nextSymbols)
  }, [instruments, selectedSymbol])

  const { data: marketDataEvents } = useMarketDataEvents({
    account: accountCode ? `default:${accountCode}` : undefined,
    symbols: subscribedSymbols,
    eventTypes: MARKET_DATA_EVENT_TYPES,
  })

  useEffect(() => {
    if (!marketDataEvents?.events?.length) return

    const batch: Record<string, { bid: number; ask: number }> = {}

    marketDataEvents.events.forEach((event) => {
      const bid = event.bid
      const ask = event.ask
      const symbol = event.symbol
      if (!symbol || bid == null || ask == null) return
      batch[symbol] = { bid, ask }
    })

    if (Object.keys(batch).length) {
      initializeCurrentPrices(batch)
    }
  }, [marketDataEvents])

  const { candlesBySymbol: latestDailyCandles } = useDxtradeLatestCandles('Day', subscribedSymbols)
  const { sparklineBySymbol, fetchedSymbols: sparklineFetchedSymbols } =
    useSparklineData(subscribedSymbols)

  useEffect(() => {
    if (!isMobile) {
      mobileObserverRef.current?.disconnect()
      return
    }

    const target = mobileLoadMoreRef.current
    if (!target) return

    mobileObserverRef.current?.disconnect()

    mobileObserverRef.current = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry?.isIntersecting) {
          if (edgeLockRef.current === 'bottom') {
            edgeLockRef.current = null
          }
          return
        }

        if (isInitialLoading || isLoadingMore || !hasNext) return
        if (edgeLockRef.current === 'bottom') return

        edgeLockRef.current = 'bottom'
        loadNext()
      },
      { threshold: 0.1, rootMargin: '64px 0px 0px 0px' }
    )

    mobileObserverRef.current.observe(target)

    return () => {
      mobileObserverRef.current?.disconnect()
    }
  }, [isMobile, hasNext, isInitialLoading, isLoadingMore, loadNext])

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const { scrollTop, clientHeight, scrollHeight } = event.currentTarget
      if (isInitialLoading || isLoadingMore) {
        return
      }

      const previousTop = lastScrollTopRef.current
      lastScrollTopRef.current = scrollTop

      const isScrollingDown = scrollTop > previousTop
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - BOTTOM_LOAD_THRESHOLD_PX

      if (!isNearBottom && edgeLockRef.current === 'bottom') {
        edgeLockRef.current = null
      }

      if (isScrollingDown && isNearBottom && hasNext && edgeLockRef.current !== 'bottom') {
        edgeLockRef.current = 'bottom'
        loadNext()
      }
    },
    [hasNext, isInitialLoading, isLoadingMore, loadNext]
  )

  const isTableEmpty = !isInitialLoading && instruments.length === 0
  const noResultsContent = watchlistEmptyState ?? (
    <Box px={isMobile ? 'x0' : 'x16'} fullWidth>
      <TableNoResults
        title={t('Instruments.No results found')}
        content={<Body1 color='text-med-em'>{t('Instruments.Try adjusting your search')}</Body1>}
      />
    </Box>
  )

  let content: ReactNode

  if (isMobile) {
    content = isInitialLoading ? (
      <div className={styles.mobileList}>
        {Array.from({ length: 5 }).map((_, index) => (
          <InstrumentCardSkeleton key={index} />
        ))}
      </div>
    ) : isTableEmpty ? (
      <div className={styles.mobileList}>{noResultsContent}</div>
    ) : (
      <div className={styles.mobileList}>
        {instruments.map((instrument, index) => {
          const key = instrument.symbol ?? `${instrument.type ?? 'instrument'}-${index}`
          return (
            <InstrumentCard
              key={key}
              instrument={instrument}
              onAction={onInstrumentAction}
              isFavorite={isFavoriteInstrument(instrument.symbol, favoriteSymbols)}
              onToggleFavorite={onToggleFavorite}
              latestDailyCandle={
                instrument.symbol ? latestDailyCandles[instrument.symbol] : undefined
              }
              sparklinePrices={instrument.symbol ? sparklineBySymbol[instrument.symbol] : undefined}
              isSparklineLoading={
                !!instrument.symbol && !sparklineFetchedSymbols.has(instrument.symbol)
              }
              isFirstCard={index === 0}
            />
          )
        })}
        {isLoadingMore && <InstrumentCardSkeleton />}
        {hasNext && (
          <div ref={mobileLoadMoreRef} className={styles.mobileLoadMoreSpacer} aria-hidden />
        )}
      </div>
    )
  } else {
    content = (
      <div
        className={classNames(styles.tableScrollAreaWrapper, {
          [styles.hasHorizontalScrollbar]: isHorizontalScrollable,
        })}
      >
        <div
          ref={scrollContainerRef}
          className={classNames(styles.tableScrollArea, {
            [styles.tableScrollAreaNoScroll]: isTableEmpty,
            [styles.tableScrollAreaExpanded]: collapsed,
          })}
          onScroll={handleScroll}
        >
          <Table
            isLoading={isInitialLoading}
            noResults={isTableEmpty ? noResultsContent : undefined}
            skeletonRows={5}
            skeletonColumns={6}
          >
            <TableBody>
              {instruments.map((instrument, index) => {
                const key = instrument.symbol ?? `${instrument.type ?? 'instrument'}-${index}`
                return (
                  <InstrumentRow
                    onToggleCollapse={onToggleCollapse}
                    key={key}
                    instrument={instrument}
                    isSelected={instrument.symbol === selectedSymbol}
                    isFirstRow={index === 0}
                    latestDailyCandle={
                      instrument.symbol ? latestDailyCandles[instrument.symbol] : undefined
                    }
                    sparklinePrices={
                      instrument.symbol ? sparklineBySymbol[instrument.symbol] : undefined
                    }
                    isSparklineLoading={
                      !!instrument.symbol && !sparklineFetchedSymbols.has(instrument.symbol)
                    }
                    onAction={onInstrumentAction}
                    onSelect={onSelect}
                    isFavorite={isFavoriteInstrument(instrument.symbol, favoriteSymbols)}
                    onToggleFavorite={onToggleFavorite}
                    collapsed={collapsed}
                    isOrderWidgetOpen={isOrderWidgetOpen}
                  />
                )
              })}

              {isLoadingMore && <SkeletonRow columns={6} />}
            </TableBody>
          </Table>
        </div>
        <Scrollbar
          parentRef={scrollContainerRef}
          isScrollableOverride={isVerticalScrollable}
          orientation='vertical'
          size='sm'
        />
        <Scrollbar
          parentRef={scrollContainerRef}
          isScrollableOverride={isHorizontalScrollable}
          orientation='horizontal'
        />
      </div>
    )
  }

  return (
    <>
      <MarketDataBridge symbols={subscribedSymbols} onTick={updateCurrentPricesFromMarketData} />
      {content}
    </>
  )
}

export default InstrumentsTable

type InstrumentRowProps = {
  instrument: DxTradeInstrumentWithTradingMeta
  isSelected: boolean
  isFirstRow: boolean
  latestDailyCandle?: DxTradeRawCandle
  sparklinePrices?: number[]
  isSparklineLoading?: boolean
  onAction?: InstrumentActionHandler
  onSelect?: (symbol: string, priceIncrement?: number | null) => void
  isFavorite?: boolean
  onToggleFavorite?: (symbol: string) => void
  collapsed?: boolean
  onToggleCollapse: (collapsed: boolean) => void
  isOrderWidgetOpen: boolean
}

const InstrumentRow = memo(
  ({
    instrument,
    isSelected,
    isFirstRow,
    latestDailyCandle,
    sparklinePrices,
    isSparklineLoading,
    onAction,
    onSelect,
    isFavorite,
    onToggleFavorite,
    collapsed = true,
    onToggleCollapse,
    isOrderWidgetOpen,
  }: InstrumentRowProps) => {
    const { t } = useTranslation()

    const description = instrument.description ?? ''
    const symbol = formatSymbol(instrument.symbol, description || undefined)

    const tradingHours = instrument.tradingHours ?? null
    const hasTradingHours = (tradingHours?.length ?? 0) > 0
    const { isOpen } = useTradingSessionStatus(tradingHours)
    const isMarketClosed = hasTradingHours && !isOpen

    const handleAction = useCallback(
      (action: InstrumentActionType) => {
        if (!instrument.symbol) {
          return
        }
        const instrumentForAction = instrument as unknown as DxTradeInstrumentDetails
        onAction?.(instrumentForAction, action)
      },
      [instrument, onAction]
    )

    const handleRowClick = useCallback(() => {
      if (!instrument.symbol || !onSelect) return
      if (collapsed) {
        onToggleCollapse(!collapsed)
      }
      onSelect(instrument.symbol, instrument.priceIncrement)
    }, [collapsed, instrument.symbol, onSelect, onToggleCollapse])

    const handleToggleFavoriteClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        if (instrument.symbol && onToggleFavorite) {
          onToggleFavorite(instrument.symbol)
        }
      },
      [instrument.symbol, onToggleFavorite]
    )

    const sellPrice = useCurrentPriceForTrade(instrument.symbol ?? null, 'SELL', 'order')
    const buyPrice = useCurrentPriceForTrade(instrument.symbol ?? null, 'BUY', 'order')

    const { formatNumber } = useFormatNumber()
    const fractionDigits = getStepDecimals(instrument.priceIncrement)
    const formatPrice = (p: number | null | undefined) =>
      p != null
        ? formatNumber(p, { minDecimalPlaces: fractionDigits, maxDecimalPlaces: fractionDigits })
        : undefined
    const formattedSell = formatPrice(sellPrice)
    const formattedBuy = formatPrice(buyPrice)

    const { label: changePercentLabel, color: changePercentColor } = useChangePercent(
      sellPrice,
      latestDailyCandle?.openingPrice
    )

    return (
      <TableRow
        selected={isSelected}
        aria-selected={isSelected}
        className={styles.instrumentRow}
        data-first-row={isFirstRow ? 'true' : undefined}
        onClick={handleRowClick}
      >
        <TableCell align='left' hasTruncate={false}>
          <div className={styles.instrumentCellWrapper}>
            <div className={styles.instrumentCell}>
              <Body2>{symbol}</Body2>

              <Body2 color='text-low-em'>
                <Truncate>{description} </Truncate>
              </Body2>
            </div>
            {isMarketClosed && (
              <Tooltip pointer direction='top' content={t('Instruments.Market closed')}>
                <MoonIcon size={20} />
              </Tooltip>
            )}
          </div>
        </TableCell>
        {changePercentLabel ? (
          <TableCell align='right' width='90px'>
            <Tooltip pointer direction='top' content={t('24 hours change')}>
              <Body2 color={changePercentColor}>{changePercentLabel}</Body2>
            </Tooltip>
          </TableCell>
        ) : (
          <TableCell align='center' width='90px'>
            <Body2 color={'text-low-em'}>{PRICE_PLACEHOLDER}</Body2>
          </TableCell>
        )}

        <TableCell align='center' hasTruncate={false} width='140px'>
          {sparklinePrices ? (
            <Sparkline prices={sparklinePrices} width={120} height={32} />
          ) : isSparklineLoading ? (
            <SparklineSkeleton width={120} height={32} />
          ) : null}
        </TableCell>
        <InstrumentActionCell
          price={sellPrice}
          formattedPrice={formattedSell}
          side='SELL'
          actionType='sell'
          onAction={handleAction}
        />
        <InstrumentActionCell
          price={buyPrice}
          formattedPrice={formattedBuy}
          side='BUY'
          actionType='buy'
          onAction={handleAction}
          tourTarget={isFirstRow ? 'trade.buySell' : undefined}
        />

        <TableCell align='center' hasTruncate={false} width='1%'>
          <motion.div
            initial={false}
            animate={{ opacity: isOrderWidgetOpen ? 0 : 1 }}
            transition={{ duration: 0.1, ease: 'easeIn' }}
            style={{ pointerEvents: isOrderWidgetOpen ? 'none' : 'auto' }}
            className={classNames(styles.instrumentActions, {
              [styles.instrumentActionsHidden]: isOrderWidgetOpen,
            })}
          >
            <IconButton.Ghost
              icon={getFavoriteIcon(isFavorite)}
              onClick={handleToggleFavoriteClick}
            />
            <IconButton.Ghost
              icon={{ icon: InfoIcon, color: 'text-low-em' }}
              onClick={(event) => {
                event.stopPropagation()
                handleAction('info')
              }}
            />
            <IconButton.Ghost
              icon={{ icon: NewspaperIcon, color: 'text-low-em' }}
              onClick={(event) => {
                event.stopPropagation()
                handleAction('news')
              }}
            />
          </motion.div>
        </TableCell>
      </TableRow>
    )
  }
)

InstrumentRow.displayName = 'InstrumentRow'

type InstrumentActionCellProps = {
  side: 'BUY' | 'SELL'
  actionType: InstrumentActionType
  price?: number | null
  formattedPrice?: string
  onAction?: (action: InstrumentActionType) => void
  tourTarget?: string
}

const InstrumentActionCell = memo(
  ({
    side,
    actionType,
    price,
    formattedPrice,
    onAction,
    tourTarget,
  }: InstrumentActionCellProps) => {
    const { t } = useTranslation()
    const labelKey = side === 'BUY' ? 'Instruments.Buy' : 'Instruments.Sell'

    const ButtonComponent = actionType === 'buy' ? Button.Success : Button.Danger

    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        onAction?.(actionType)
      },
      [actionType, onAction]
    )
    const color = usePriceColor(price ?? undefined)

    return (
      <TableCell
        align='right'
        hasTruncate={false}
        className={styles.actionCell}
        data-tour-target={tourTarget}
      >
        <Box flex gap='x8' align='center'>
          <Body2 color={color}>
            <span className={styles.priceValue}>
              {formattedPrice ?? (price != null ? String(price) : PRICE_PLACEHOLDER)}
            </span>
          </Body2>
          <div className={styles.actionButtonWrapper}>
            <ButtonComponent size='sm' onClick={handleClick} fullWidth>
              {t(labelKey)}
            </ButtonComponent>
          </div>
        </Box>
      </TableCell>
    )
  }
)

InstrumentActionCell.displayName = 'InstrumentActionCell'
